import { useState } from "react";
import { del, get, patch, post } from "./api";
import { FormularioVisita, paraPayload, type EstadoFormulario } from "../components/FormularioVisita";
import { FichaImpressao } from "../components/FichaImpressao";
import { HistoricoWhatsapp } from "../components/HistoricoWhatsapp";
import { useDadosApoio } from "./dados";
import { dataHora } from "./formato";
import type { Visita } from "./tipos";

/**
 * Concentra criar/editar visita e imprimir ficha. As telas de lista só chamam
 * `abrirNova`, `abrirEdicao` e `abrirFicha`, e renderizam `modais`.
 */
export function useAcoesVisita(aoSalvar: () => void | Promise<void>) {
  const apoio = useDadosApoio();
  const [editando, setEditando] = useState<Visita | null | undefined>(undefined);
  const [imprimindo, setImprimindo] = useState<Visita | null>(null);
  const [whatsapp, setWhatsapp] = useState<Visita | null>(null);
  const [excluindo, setExcluindo] = useState<Visita | null>(null);
  const [apagando, setApagando] = useState(false);
  const [aviso, setAviso] = useState("");

  type Resposta = Visita & {
    pesquisa?: { whatsappUrl?: string };
    whatsapp?: { enviado: boolean; jaEnviado?: boolean; erro?: string };
  };

  async function salvar(f: EstadoFormulario) {
    const corpo = paraPayload(f);
    const salva = editando
      ? await patch<Resposta>(`/api/visitas/${editando.id}`, corpo)
      : await post<Resposta>("/api/visitas", corpo);

    const url = salva.pesquisa?.whatsappUrl;
    if (url) window.open(url, "_blank", "noopener");
    else if (f.status === "EM_PESQUISA") {
      setAviso("Visita salva, mas o convite de pesquisa não pôde ser enviado. Verifique a configuração do Sistema 2.");
    }

    // a confirmação sai no servidor; aqui só contamos o que aconteceu
    const w = salva.whatsapp;
    if (w && !w.enviado && !w.jaEnviado) {
      setAviso(`Visita salva, mas a confirmação por WhatsApp não saiu: ${w.erro ?? "erro desconhecido"}`);
    }

    setEditando(undefined);
    await aoSalvar();
  }

  /**
   * Tira a visita do "Precisa de você" assim que alguém abre.
   * Falhar aqui não atrapalha ninguém — no pior caso ela continua listada.
   */
  const marcarVista = (id: string) => post(`/api/visitas/${id}/vista`).catch(() => {});

  /** Abre o histórico de envios da visita, de onde também se reenvia. */
  const enviarWhatsapp = (v: Visita) => setWhatsapp(v);

  /**
   * Exclusão de verdade, sem lixeira de recuperação: some a visita, a
   * proposta, o histórico de status e o registro de WhatsApp. Por isso a
   * confirmação nomeia a família — para ninguém apagar a linha errada por
   * um clique fora do lugar.
   */
  async function confirmarExclusao() {
    if (!excluindo) return;
    setApagando(true);
    try {
      await del(`/api/visitas/${excluindo.id}`);
      setExcluindo(null);
      await aoSalvar();
    } catch (e) {
      setAviso(`Não foi possível excluir: ${e instanceof Error ? e.message : "erro desconhecido"}`);
      setExcluindo(null);
    } finally {
      setApagando(false);
    }
  }

  const modais = (
    <>
      {aviso && (
        <div style={{ position: "fixed", bottom: 20, right: 20, zIndex: 1000, maxWidth: 420, background: "#fffaeb", border: "1px solid #fec84b", color: "#b54708", padding: "12px 14px", borderRadius: 10, fontSize: 13, display: "flex", gap: 12, boxShadow: "0 8px 24px rgba(0,0,0,.12)" }}>
          <span style={{ flex: 1 }}>{aviso}</span>
          <button onClick={() => setAviso("")} style={{ background: "none", border: "none", cursor: "pointer", color: "inherit" }}>✕</button>
        </div>
      )}
      {editando !== undefined && (
        <FormularioVisita
          visita={editando}
          apoio={apoio}
          onSalvar={salvar}
          onCancelar={() => setEditando(undefined)}
        />
      )}
      {imprimindo && (
        <FichaImpressao
          visita={imprimindo}
          catalogo={apoio.diferenciais}
          onFechar={() => setImprimindo(null)}
        />
      )}
      {whatsapp && (
        <HistoricoWhatsapp
          visita={whatsapp}
          onFechar={() => setWhatsapp(null)}
          aoEnviar={aoSalvar}
        />
      )}
      {excluindo && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 980, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 440, padding: "22px 24px", boxShadow: "0 24px 80px rgba(0,0,0,.25)" }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 17, color: "#b42318" }}>Excluir esta visita?</h3>
            <p style={{ fontSize: 14, color: "#344054", lineHeight: 1.6, margin: "0 0 12px" }}>
              <strong>{excluindo.nomeAluno}</strong> — {excluindo.nomeResponsavel}
              <br />
              {dataHora(excluindo.inicioEm)}
            </p>
            <p style={{ fontSize: 12.5, color: "#b54708", background: "#fffaeb", border: "1px solid #fedf89", borderRadius: 8, padding: "10px 12px", lineHeight: 1.6, margin: "0 0 18px" }}>
              Some junto a proposta com os valores, o histórico de status e o registro das
              mensagens de WhatsApp. <strong>Não há como desfazer.</strong>
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="botao secundario" onClick={() => setExcluindo(null)} disabled={apagando}>
                Cancelar
              </button>
              <button
                onClick={confirmarExclusao}
                disabled={apagando}
                style={{ background: "#d92d20", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
              >
                {apagando ? "Excluindo..." : "Excluir"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return {
    abrirNova: () => setEditando(null),
    abrirEdicao: (v: Visita) => {
      setEditando(v);
      if (v.origemPublica && !v.vistaEm) void marcarVista(v.id);
    },
    /**
     * Abre a edição a partir do id. As listas de pendência do dashboard só
     * carregam um resumo da visita — abrir o formulário com esse resumo
     * apagaria a proposta inteira ao salvar, então busca a visita completa.
     */
    abrirEdicaoPorId: async (id: string) => {
      try {
        const v = await get<Visita>(`/api/visitas/${id}`);
        setEditando(v);
        if (v.origemPublica && !v.vistaEm) void marcarVista(v.id);
      } catch (e) {
        setAviso(`Não foi possível abrir a visita: ${e instanceof Error ? e.message : "erro"}`);
      }
    },
    abrirFicha: (v: Visita) => setImprimindo(v),
    enviarWhatsapp,
    excluir: (v: Visita) => setExcluindo(v),
    modais,
  };
}
